import React, { Suspense } from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import Scales from './modules/scales/containers/Scales';
import { Features } from './interfaces/company.interface';
import SessionUserProfileRedirect from './modules/reports/SessionUserProfileRedirect';
import Login from './outer_modules/login/Login';
import ActivateAccount from './outer_modules/activateAccount/ActivateAccount';
import Accounts from './outer_modules/login/components/Accounts';
import ContactUs from './modules/contactUs/ContactUs';
import Otp from './outer_modules/otp/Otp';
import RestorePassword from './outer_modules/restorePassword/RestorePassword';
import NoSurvey from './modules/questions/components/NoSurvey';
import Topics from './modules/topics/containers/Topics';
import Logout from './outer_modules/logout/Logout';
import NewPassword from './outer_modules/newPassword/NewPassword';
import Surveys from './modules/surveys/containers/Surveys';
import Attributes from './modules/attributes/containers/Attributes';
import UserSettings from './modules/userSettings/UserSettings';
import QuestionsRouter from './modules/questions/containers/QuestionsRouter';
import UserManagement from './modules/users/containers/UserManagement';
import Authenticated from './shared/routes/Authenticated';
import Public from './shared/routes/Public';
import { LoaderContainer } from './shared/sidebar/styles/sidebar.sc';
import PageLoader from './shared/pageLoader/PageLoader';
import PageNotFound from './shared/errorPages/PageNotFound';
import ErrorCatchPage from './shared/errorPages/ErrorCatchPage';
import MobileOverlay from './shared/mobileOverlay/MobileOverlay';
import logger from './services/logger/LlsLogger';
import { UserPermissions } from './interfaces/session.types';
import AccountSuspended from './outer_modules/accountSuspended/AccountSuspended';
import Compare from '~src/modules/heatmap/Heatmap';
import { isFullPageRoute, routes } from './routingConfig';
import { GlobalMarginContainer } from './styles/containers.sc';
import OkrRouter from './modules/okrs/OkrRouter';
import LoadingSpinner from './shared/loadingSpinner/LoadingSpinner';
import CompanySettingsRouter from './modules/companySettings/CompanySettings.router';
import UserHome from './modules/userHome/userHome';
import { Spaceloop } from './modules/spaceloop/Spaceloop';
import Recommendations from '~src/modules/recommendations/Recommendations';
import SpiderGraph from './modules/spidergraph/Spidergraph';
import SelfSignup from './outer_modules/login/SelfSignup';
import PerformanceManagement from './modules/companySettings/performanceManagement/PerformanceManagement';
import AdminFeedbackCyclePage from './modules/companySettings/performanceManagement/AdminFeedbackCyclePage/AdminFeedbackCyclePage';

// TODO: remove after tests
// const loadReports = () => import(/* webpackChunkName: "reports" */ './modules/reports/ReportsRouter');
// const ReportsRouter = (props: any) => <DynamicImport load={loadReports} passedProps={props} />;

const ReportsRouter = React.lazy(
  () => import(/* webpackChunkName: "reports" */ './modules/reports/ReportsRouter'),
);

interface Props extends Pick<RouteComponentProps<object>, 'location' | 'history'> {
  isAuthenticated: boolean;
  mobile: boolean;
  sidebarHidden: boolean;
  features: Partial<Features>;
}

interface State {
  error?: {
    errorMsg: Error;
    infoMsg: React.ErrorInfo;
  };
  location: string | null;
}

export function GetDefaultRedirect(isAuthenticated: boolean, features: Partial<Features>) {
  return function DefaultRedirectComponent() {
    let to = routes.login;
    if (isAuthenticated) {
      if (features.survey) {
        to = routes.sessionUser.home;
      } else if (features.okr) {
        to = routes.okrs.list;
      } else {
        to = routes.sessionUser.settings;
      }
    }
    return <Redirect to={to} />;
  };
}

class PagesRoutes extends React.Component<Props, State> {
  static getDerivedStateFromProps(props: Props, state: State) {
    if (state.error && props.location.pathname !== state.location) {
      return {
        error: undefined,
        location: props.location.pathname,
      };
    }
    return null;
  }

  state = {
    error: undefined,
    location: null,
  };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error('FE ERROR', { error, info });
    this.setState({
      error: {
        errorMsg: error,
        infoMsg: info,
      },
      location: this.props.location.pathname,
    });
  }

  renderLoader = () => (
    <LoaderContainer>
      <PageLoader />
    </LoaderContainer>
  );

  render() {
    const okrUnlocked = Boolean(this.props.features.okr);
    const surveyUnlocked = Boolean(this.props.features.survey);
    const recommendationsUnlocked = Boolean(this.props.features.survey?.recommendations);

    const { isAuthenticated, sidebarHidden } = this.props;
    // THE ORDER OF THE ROUTES IS IMPORTANT PLEASE DO NOT CHANGE IT
    return (
      <GlobalMarginContainer
        $sidebarHidden={isFullPageRoute() || sidebarHidden}
        $isAuthenticated={isFullPageRoute() || isAuthenticated}
      >
        <Suspense fallback={<LoadingSpinner alignCls="center-middle" />}>
          <Switch>
            {this.state.error && <ErrorCatchPage />}
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                path={routes.answerQuestions.noSurvey}
                component={NoSurvey}
                requiredPermissions={[]}
                {...this.props}
              />
            )}
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                path={routes.answerQuestions.questions}
                component={QuestionsRouter}
                requiredPermissions={[UserPermissions.ManageMe]}
                {...this.props}
              />
            )}
            <Authenticated
              exact
              path="/accounts"
              component={Accounts}
              requiredPermissions={[]}
              {...this.props}
            />
            <Authenticated
              exact
              path={routes.logout}
              component={Logout}
              requiredPermissions={[]}
              isAuthenticated={isAuthenticated}
            />
            <Public
              exact
              path={[routes.login, '/signup']}
              component={Login}
              isAuthenticated={isAuthenticated}
            />
            <Public
              exact
              path="/forgot_password"
              component={RestorePassword}
              isAuthenticated={isAuthenticated}
            />
            <Route exact path="/spaceloop" component={Spaceloop} />
            <Route exact path="/activate" component={ActivateAccount} />
            <Route exact path="/password" component={NewPassword} />
            <Route exact path={routes.contact} component={ContactUs} />
            <Route exact path={routes.accountSuspended} component={AccountSuspended} />
            <Route exact path="/loading" render={this.renderLoader} />
            <Route exact path="/otp" component={Otp} />
            <Route path={'/users/signup'} component={SelfSignup} />
            <Route exact path="/" component={GetDefaultRedirect(isAuthenticated, this.props.features)} />
            {(!isAuthenticated || okrUnlocked) && (
              <Authenticated
                path={routes.okrs.list}
                component={OkrRouter}
                requiredPermissions={[UserPermissions.ManageMe]}
                {...this.props}
              />
            )}
            {this.props.mobile && this.props.isAuthenticated && <MobileOverlay />}
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                path="/reports"
                component={ReportsRouter}
                requiredPermissions={[]}
                {...this.props}
              />
            )}
            <Authenticated
              path={routes.company.attributes}
              component={Attributes}
              requiredPermissions={[UserPermissions.ManageCompany]}
              {...this.props}
            />
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                path={routes.contentSurveys.surveys}
                component={Surveys}
                requiredPermissions={[]}
                {...this.props}
              />
            )}
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                path={routes.contentTopics.topics}
                component={Topics}
                requiredPermissions={[UserPermissions.ManageCompany]}
                {...this.props}
              />
            )}
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                path={routes.contentScales.scales}
                component={Scales}
                requiredPermissions={[UserPermissions.ManageCompany]}
                {...this.props}
              />
            )}
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                exact
                path={routes.reports.sessionUser}
                component={SessionUserProfileRedirect}
                requiredPermissions={[]}
                {...this.props}
              />
            )}
            <Authenticated
              exact
              path={routes.company.users}
              component={UserManagement}
              requiredPermissions={[UserPermissions.ManageCompany]}
              {...this.props}
            />
            <Authenticated
              exact
              path={routes.sessionUser.settings}
              component={UserSettings}
              requiredPermissions={[UserPermissions.ManageMe]}
              {...this.props}
            />
            <Authenticated
              exact
              path={routes.sessionUser.home}
              component={UserHome}
              requiredPermissions={[]}
              {...this.props}
            />
            <Authenticated
              path={routes.company.settings}
              component={CompanySettingsRouter}
              requiredPermissions={[UserPermissions.ManageCompany]}
              {...this.props}
            />
            <Authenticated
              path={routes.contentScales.performanceManagement}
              component={PerformanceManagement}
              requiredPermissions={[UserPermissions.ManageCompany]}
              {...this.props}
            />
            <Authenticated
              path={routes.contentScales.adminFeedbackCycle(':id')}
              component={AdminFeedbackCyclePage}
              requiredPermissions={[UserPermissions.ManageCompany]}
              {...this.props}
            />
            <Route exact path="/compare">
              <Redirect to={{ pathname: routes.analysis.heatmap }} />
            </Route>
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                exact
                path={routes.analysis.heatmap}
                component={Compare}
                requiredPermissions={[UserPermissions.ManageMe]}
                {...this.props}
              />
            )}
            {(!isAuthenticated || surveyUnlocked) && (
              <Authenticated
                path={routes.analysis.spider}
                component={SpiderGraph}
                requiredPermissions={[UserPermissions.ManageMe]}
                {...this.props}
              />
            )}
            {(!isAuthenticated || surveyUnlocked) && recommendationsUnlocked && (
              <Authenticated
                path={routes.analysis.ai}
                component={Recommendations}
                requiredPermissions={[UserPermissions.ManageMe]}
                {...this.props}
              />
            )}
            <PageNotFound {...this.props} />
          </Switch>
        </Suspense>
      </GlobalMarginContainer>
    );
  }
}

export default PagesRoutes;
